import { BetweenAccountsSendMoneyValueLabel, MMButton } from '@components'
import { formatCurrencyForDisplay } from '@utils'
import styles from '@withdrawals/styles/WithdrawPreview.module.scss'
import { useCurrencies } from 'mmfintech-backend-api'
import type { AccountBalanceResponse, PaymentPreview, SimpleCallback } from 'mmfintech-backend-api/types'
import { formatFloat, tr } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'

const TKB_DISCOUNT_RATE = 0.25
const TKB_CURRENCY_CODE = 'TKB.ERC20'

type IntraAccountTransferPreviewProps = {
  sourceAccount: AccountBalanceResponse
  targetAccount: AccountBalanceResponse
  previewData: PaymentPreview
  initiateError: unknown
  initiateLoading: boolean
  onContinue: SimpleCallback
  onCancel: SimpleCallback
  tkbDiscountEligible?: boolean
  payFeeFromTkb?: boolean
}

const IntraAccountTransferPreview = (props: IntraAccountTransferPreviewProps) => {
  const {
    sourceAccount,
    targetAccount,
    previewData,
    initiateError,
    initiateLoading,
    onContinue,
    onCancel,
    tkbDiscountEligible,
    payFeeFromTkb
  } = props

  const { formatFxRate, getCurrencyPrecision } = useCurrencies()

  const feeIsTkb = previewData?.feeCurrency === TKB_CURRENCY_CODE
  const showDiscount = Boolean(tkbDiscountEligible && payFeeFromTkb && previewData?.fee && feeIsTkb)
  const showTkbFallback = Boolean(tkbDiscountEligible && payFeeFromTkb && previewData?.fee && !feeIsTkb)
  const rawFee = previewData?.fee ?? 0
  const originalFee = showDiscount ? rawFee / (1 - TKB_DISCOUNT_RATE) : rawFee

  return (
    <div className={styles['withdraw-preview-wrapper']}>
      <div>
        <div className='withdraw-preview-heading'>
          {tr('FRONTEND.WITHDRAW.INTRA_ACCOUNT_TRANSFER.PREVIEW.FROM', 'From')}
        </div>
        <div className='withdraw-preview-container send-money-details'>
          <BetweenAccountsSendMoneyValueLabel
            account={sourceAccount}
            walletAmount={previewData?.walletAmount}
            walletCurrency={previewData?.walletCurrency}
            dataTest='source'
          />
        </div>
      </div>

      <div>
        <div className='withdraw-preview-heading'>
          {tr('FRONTEND.WITHDRAW.INTRA_ACCOUNT_TRANSFER.PREVIEW.TO', 'To')}
        </div>
        <div className='withdraw-preview-container send-money-details'>
          <BetweenAccountsSendMoneyValueLabel
            account={targetAccount}
            toAmount={previewData?.toAmount ?? previewData?.processingAmount}
            processingCurrency={previewData?.toCurrency ?? previewData?.processingCurrency}
            dataTest='target'
          />
        </div>
      </div>

      <div>
        {Boolean(previewData?.fxRate) && previewData.fxRate !== 1 && (
          <div className='withdraw-preview-rate'>
            <span className='withdraw-preview-rate-label'>
              <em>&#9679;</em> {tr('FRONTEND.WITHDRAW.INTRA_ACCOUNT_TRANSFER.PREVIEW.EXCHANGE_RATE', 'Exchange rate')}:{' '}
            </span>
            <span className='withdraw-preview-rate-content'>
              {'1 '}
              <span className='withdraw-preview-currency'>{formatCurrencyForDisplay(previewData?.walletCurrency)}</span>
              {' = '}
              {formatFxRate(previewData?.fxRate, previewData?.toCurrency)}{' '}
              <span className='withdraw-preview-currency'>{formatCurrencyForDisplay(previewData?.toCurrency)}</span>
            </span>
          </div>
        )}

        {Boolean(previewData?.fee) && (
          <div className='withdraw-preview-fee'>
            <span className='withdraw-preview-fee-label'>
              <em>&#9679;</em> {tr('FRONTEND.WITHDRAW.INTRA_ACCOUNT_TRANSFER.PREVIEW.FEE', 'Fee')}:
            </span>
            <span className='withdraw-preview-fee-content'>
              {showDiscount && (
                <span className='withdraw-preview-fee-original'>
                  {formatFloat(originalFee, getCurrencyPrecision(previewData?.feeCurrency))}{' '}
                  <span className='withdraw-preview-currency'>
                    {formatCurrencyForDisplay(previewData?.feeCurrency)}
                  </span>
                </span>
              )}
              <span className='withdraw-preview-fee-amount'>
                {formatFloat(rawFee, getCurrencyPrecision(previewData?.feeCurrency))}{' '}
                <span className='withdraw-preview-currency'>{formatCurrencyForDisplay(previewData?.feeCurrency)}</span>
              </span>
              {showDiscount && (
                <span className='withdraw-preview-fee-source'>
                  {tr('FRONTEND.WITHDRAW.INTRA_ACCOUNT_TRANSFER.PREVIEW.FEE_FROM_TKB', 'Paid from Kingdom Coin')}
                </span>
              )}
              {showTkbFallback && (
                <span className='withdraw-preview-fee-source'>
                  {tr(
                    'FRONTEND.WITHDRAW.INTRA_ACCOUNT_TRANSFER.PREVIEW.FEE_TKB_FALLBACK',
                    'Insufficient Kingdom Coin balance — fee charged in fee currency'
                  )}
                </span>
              )}
            </span>
          </div>
        )}
      </div>

      <ErrorDisplay error={initiateError} />

      <div className='preview-actions'>
        <MMButton
          type='button'
          size='large'
          variant='alternative'
          caption={tr('FRONTEND.BUTTONS.CONTINUE', 'Continue')}
          onClick={onContinue}
          loading={initiateLoading}
          disabled={initiateLoading}
          data-test='continue-button'
          fullWidth
        />
        <MMButton
          type='button'
          size='large'
          variant='tertiary'
          caption={tr('FRONTEND.BUTTONS.CANCEL', 'Cancel')}
          onClick={onCancel}
          disabled={initiateLoading}
          data-test='button-cancel'
          fullWidth
        />
      </div>
    </div>
  )
}

export default IntraAccountTransferPreview
